
import Vue from 'vue'
import * as Papa from 'papaparse'
import * as Helpers from '@/services/SchoolStudentHelpers'
import * as Api from '@/api/SchoolSettingsApi'
import {SchoolStudentDto} from '@ViewModels/SchoolStudentDto'
import {ClassRoomDto} from '@ViewModels/ClassRoomDto'
import {DataSet, ODataService} from '@/services'
import {RouteButton, SaveButton} from '@/components/buttons'
import {CogMenu} from '@/components'
import {PageHeader} from '../components'
import {FilterDropdown, FilterMultiselect, FilterText, FilterToggleListItem, PagedDataTable,} from '@/components/lists'
import SelectionPanel from '../components/StudentListSelectionPanel.vue'

export default Vue.extend({
   name: 'StudentList',
   props: {
      settings: { type: Boolean, default: false}
   },
   data() {
      let schoolYearStr = this.$route.query.schoolYear || null
      if (schoolYearStr !== null && schoolYearStr !== undefined && schoolYearStr !== '') 
      {
         this.$store.dispatch('setSchoolYearFilter', +schoolYearStr)
      }
      // return empty data      
      return {
         dataset: new DataSet(new ODataService('SchoolStudent')) as IDataSet,
         schoolYearOptions: [] as NameValuePair[],
         classrooms: [] as ClassRoomDto[],
         loadedInitialClassroomsForTeacher: false,
         columns: [
            { name: 'FirstName', title: 'First Name' },
            { name: 'LastName', title: 'Last Name' },
            { name: 'ClassRoomNames', title: 'Classes', type: 'list', sortable: false },
            { name: 'Gender', title: 'Gender' },
            { name: 'DateOfBirth', title: 'Date of Birth', type: 'date' },
            { name: 'TargetTypes', title: 'Target', type: 'targettypes', sortable: false },
            { name: 'IsEnabled', title: 'Active', type: 'boolean-hide' },
         ] as IDataTableColumn[],
         downloading: false,
         crumbs: [{ text: 'Students' }] as Breadcrumb[],
      }
   },
   computed: {
      activeSchoolId(): string {
         return this.$store.getters.activeSchoolId
      },
      activeSchoolYear(): number {
         return this.$store.getters.schoolYearFilter
      },
      selectedClassrooms(): string[] {
         //console.log('selectedClassrooms', this.$store.getters.classroomIdsFilter)
         const allSelectedClassroomIds = this.$store.getters.classroomIdsFilter as string[]
         const visibleClassroomIds = this.classroomOptions.map(cro => cro.value)
         const filtered = allSelectedClassroomIds.filter(classroomId => visibleClassroomIds.includes(classroomId))
         //console.log('/selectedClassrooms', filtered)
         return filtered
      },
      classroomOptions(): NameValuePair[] {
        //console.log('classroomOptions', this.activeSchoolYear, ', ', this.classrooms)
        const classes = this.classrooms
             .filter((item: ClassRoomDto) => +item.SchoolYear === +this.activeSchoolYear)
             .map(
                 (item: ClassRoomDto) =>
                     ({
                       name: item.Name,
                       value: item.Id,
                     } as NameValuePair)
             )
             .sort((a: NameValuePair, b: NameValuePair) => a.name.localeCompare(b.name))
        //console.log('/classroomOptions', classes)
        return classes
      },
   },

   created() {
   },
   async mounted() {
      this.dataset.setPageSize(50)
      await this.loadFilterOptions()
      this.dataset.sortColumn = 'FirstName'
      this.dataset.query = [{ name: 'schoolId', value: this.activeSchoolId },{name: 'schoolYear', value: this.activeSchoolYear}]
      this.dataset.expand = ['ReferenceValues', 'TargetTypes']
      this.dataset.filters = [
         { name: 'FirstName', comparison: 'substring', value: '' },
         { name: 'IsEnabled', comparison: 'eq', value: 'true' },
         { name: 'ClassRoomIds', comparison: 'any', value: [...this.selectedClassrooms], type: 'guidlist' },
      ]
      await this.reload()
   },
   methods: {
     select: function (row: SchoolStudentDto) {
         const studentId = row.Id as string
         const student = this.dataset.items.find(s => s.Id === studentId)
         this.$router.push({
           name: 'student-details',
           params: {
             studentId,
             student,
           },
         })
     },

      async schoolYearFilterChanged(value: number) {
         //console.log('schoolYearFilterChanged', value)
         if (value && value != this.activeSchoolYear) {
           //console.log('setting school year to ' + value)
           this.dataset.query = [{name: 'schoolId', value: this.activeSchoolId}, {
             name: 'schoolYear',
             value: value
           }]
           // load the classrooms for the new school year
           if (this.loadedInitialClassroomsForTeacher) {
             //console.log('loading classrooms for school year ' + value)
             // reload the classrooms, but only if we have already loaded the initial classrooms for the teacher
             // this should trigger a reload of the selection panel
             this.classrooms = await Api.getClassroomsForYear(this.activeSchoolId, value)
             //console.log('loaded classrooms for school year ' + value)
           }
           // update the school year filter
           await this.$store.dispatch('setSchoolYearFilter', value)
           // reset the classroom filter if we have already loaded the initial classrooms for the teacher
           //if (this.loadedInitialClassroomsForTeacher) {
           //  await this.loadClassrooms()
           //}
         }
         //console.log('/schoolYearFilterChanged', value)
      },

      async classroomFilterChanged(value: string[]) {
         //console.log('classroomFilterChanged', this.activeSchoolId, ', ', this.activeSchoolYear, ', ', value )
         await this.$store.dispatch('setClassroomIdsFilter', value)
         this.dataset.updateFilter('ClassRoomIds', value)
         this.dataset.showPage(1)
         await this.reload()
         //console.log('/classroomFilterChanged')
      },
     
      async filterTextChanged(value: string) {
         this.dataset.updateFilter('FirstName', value.trim())
         this.dataset.showPage(1)
         await this.reload()
      },

      async filterInactiveChanged(showInactive: boolean) {
         if (showInactive) {
            this.dataset.updateFilter('IsEnabled', '')
         } else {
            this.dataset.updateFilter('IsEnabled', 'true')
         }
         this.dataset.showPage(1)
         await this.reload()
      },

      async reload() {
        //console.log('reload', this.activeSchoolId, ', ', this.activeSchoolYear )
        await this.dataset.load()
        //console.log('/reload', this.activeSchoolId, ', ', this.activeSchoolYear )
      },
     
     async loadFilterOptions() {
         //console.log('loadFilterOptions', this.activeSchoolId, ', ', this.activeSchoolYear, ', ', this.loadedInitialClassroomsForTeacher )
         this.classrooms = await Api.getClassroomsForYear(this.activeSchoolId, this.activeSchoolYear)
         // on the initial load, if the classroom filter is empty, then set it to the classrooms for the active teacher
         if (this.$store.getters.classroomIdsFilter.length == 0) 
         {
             const activeTeacher = this.$store.getters.activeSchoolTeacherId
             const activeTeacherClasses = this.classrooms
                .filter(cr => cr.SchoolTeacherIds.includes(activeTeacher))
                .map(cr => cr.Id)
                .filter(v => v != null && v)
    
             await this.$store.dispatch('setClassroomIdsFilter', activeTeacherClasses)
             this.dataset.updateFilter('ClassRoomIds', activeTeacherClasses as string[])
         }
         this.loadedInitialClassroomsForTeacher = true

         // Ensure the current year is one of the options
         const schoolYears = await Api.getSchoolYearOptions(this.activeSchoolId)
         const currentYear = new Date().getFullYear()
         if (schoolYears.find(y => y.value === currentYear) === null) {
            schoolYears.push({ name: currentYear.toString(), value: currentYear })
         }
         this.schoolYearOptions = schoolYears
         // display classes      
         this.loadedInitialClassroomsForTeacher = true
         //console.log('/loadFilterOptions')
      },

      async downloadCSV() {
         this.downloading = true
         try {
            const ethnicityOptions = await Api.getEthnicityOptions(this.activeSchoolId)
            const items = Helpers.generateCsv(this.dataset.items, ethnicityOptions)
            const csv = Papa.unparse(items)
            Helpers.download('students.csv', csv)
         } finally {
            this.downloading = false
         }
      },
   },
   components: {
      PageHeader,
      PagedDataTable,
      FilterText,
      FilterDropdown,
      FilterMultiselect,
      FilterToggleListItem,
      RouteButton,
      SaveButton,
      SelectionPanel,
      CogMenu,
   },
})
